import type { RouteObject } from 'react-router-dom';
import { Navigate, useRoutes } from 'react-router-dom';
import { lazyImport } from '@/utils/lazyImport';
import { Landing } from '@/features/landing';
import { CheckEmailMessage, GuestFormPage, Wizard } from '@/features/wizard';
import { useAuth } from '@/hooks';

import { protectedRoutes } from './protected';
import { publicRoutes } from './public';
import { ROUTE_PATHS } from '@/constants';
import { Loader } from '@/components/shared';
import {
  PasswordRecover,
  UserLogin,
  RecoveryPasswordSuccess,
  PasswordResetForm,
  FullSignup,
  UserSignUpConfirmation,
  PasswordRecoveryConfirmation,
} from '@/features/users';

const { UserInfoStep } = lazyImport(async () => import('@/features/wizard'), 'UserInfoStep');

const { AuctionHouseInfoStep } = lazyImport(
  async () => import('@/features/wizard'),
  'AuctionHouseInfoStep'
);

const { ShippingInfoStep } = lazyImport(
  async () => import('@/features/wizard'),
  'ShippingInfoStep'
);

const { SummaryStep } = lazyImport(async () => import('@/features/wizard'), 'SummaryStep');
const { QuoteDetails } = lazyImport(async () => import('@/features/wizard'), 'QuoteDetails');
const { PaymentSuccess } = lazyImport(async () => import('@/features/wizard'), 'PaymentSuccess');
const { PaymentError } = lazyImport(async () => import('@/features/wizard'), 'PaymentError');

const { RequestedConfirmationStep } = lazyImport(
  async () => import('@/features/wizard'),
  'RequestedConfirmationStep'
);

const filterRoutesByPath = (routes: RouteObject[], routeNames: string[]): RouteObject[] =>
  routes.filter((r) => !routeNames.includes(r.path ?? ''));

export const AppRoutes = (): JSX.Element => {
  const { authUser, isLoading } = useAuth();

  const isLogged = authUser?.isValid();
  const routes = isLogged ? protectedRoutes : publicRoutes;

  const userAccessComponent = isLogged ? (
    <Navigate to={`/${ROUTE_PATHS.user}/${ROUTE_PATHS.userDashboard}`} />
  ) : (
    <UserLogin />
  );

  const wizardRoutes: RouteObject[] = [
    { path: ROUTE_PATHS.quoteUserInfo, element: <UserInfoStep /> },
    { path: ROUTE_PATHS.quoteAuctionHouseInfo, element: <AuctionHouseInfoStep /> },
    { path: ROUTE_PATHS.quoteShippingInfo, element: <ShippingInfoStep /> },
    { path: ROUTE_PATHS.quoteSummary, element: <SummaryStep /> },
    { path: ROUTE_PATHS.quoteRequestedConfirmation, element: <RequestedConfirmationStep /> },
    { path: ROUTE_PATHS.quotePaymentSuccess, element: <PaymentSuccess /> },
    { path: ROUTE_PATHS.quotePaymentError, element: <PaymentError /> },
    { path: ROUTE_PATHS.checkEmailMessage, element: <CheckEmailMessage /> },

    {
      index: true,
      element: (
        <Navigate to={isLogged ? ROUTE_PATHS.quoteAuctionHouseInfo : ROUTE_PATHS.quoteUserInfo} />
      ),
    },
    { path: '*', element: <Navigate to="." /> },
  ];

  const commonRoutes = [
    { path: ROUTE_PATHS.landing, element: <Landing /> },
    {
      path: ROUTE_PATHS.quote,
      element: <Wizard isLogged={isLogged} />,
      children: filterRoutesByPath(wizardRoutes, isLogged ? [ROUTE_PATHS.quoteUserInfo] : []),
    },
    { path: ROUTE_PATHS.userLogin, element: userAccessComponent },
    { path: ROUTE_PATHS.userSignUpConfirmation, element: <UserSignUpConfirmation /> },
    { path: ROUTE_PATHS.fullSignUp, element: <FullSignup /> },
    { path: ROUTE_PATHS.passwordRecover, element: <PasswordRecover /> },
    { path: ROUTE_PATHS.passwordRecoveryConfirmation, element: <PasswordRecoveryConfirmation /> },
    { path: ROUTE_PATHS.passwordResetForm, element: <PasswordResetForm /> },
    { path: ROUTE_PATHS.checkEmailPasswordRecover, element: <RecoveryPasswordSuccess /> },
    { path: ROUTE_PATHS.quoteDetails, element: <QuoteDetails /> },
    { path: ROUTE_PATHS.manageDetails, element: <GuestFormPage /> },
    { path: '*', element: <p>Not found</p> },
  ];

  const element = useRoutes([...routes, ...commonRoutes]);

  if (isLoading) return <Loader />;

  return <>{element}</>;
};
